/**
 * Why an N/A ("optional") answer option is only allowed on a standard question is
 * documented in apps/backend/src/repositories/product/README.md. This module owns the
 * *decision* half of that guardrail as pure functions so it can be unit tested: the
 * admin panel (apps/admin/src/admin/handlers/validateNaOption) has no test
 * infrastructure, and both it and the backend depend on this package.
 */
/**
 * Mirrors `PrivacyIssueType.STANDARD` in packages/db, which both the entity and the AdminJS
 * entity declare as `'standard'`. Deliberately NOT imported: this package is bundled into the
 * web app, and reaching for the enum means pulling a TypeORM-decorated entity module in to read
 * one string literal - the same reason apps/web mirrors the scoring-exclusion invariant rather
 * than importing it. The backend unit test feeds the real enum member through
 * `checkNaOptionParent`, so the two cannot drift silently.
 */
const STANDARD_PRIVACY_ISSUE_TYPE = 'standard';
export var NaOptionParentRejection;
(function (NaOptionParentRejection) {
    /** The submission carried no parent question at all. */
    NaOptionParentRejection["MISSING_PARENT_ID"] = "missing-parent-id";
    /** A parent id was submitted but no question could be loaded for it. */
    NaOptionParentRejection["UNRESOLVED_PARENT"] = "unresolved-parent";
    /** The parent question resolved, but N/A is not permitted on its type. */
    NaOptionParentRejection["NON_STANDARD_PARENT"] = "non-standard-parent";
})(NaOptionParentRejection || (NaOptionParentRejection = {}));
export const NA_OPTION_PARENT_REJECTION_MESSAGES = {
    [NaOptionParentRejection.MISSING_PARENT_ID]: 'An N/A (optional) option must be created from its parent question\'s Show view, so the question it belongs to is known. This submission carried no parent question.',
    [NaOptionParentRejection.UNRESOLVED_PARENT]: 'The parent question for this option could not be found, so it cannot be checked that N/A is allowed on it. Create the option from its parent question\'s Show view.',
    [NaOptionParentRejection.NON_STANDARD_PARENT]: 'An N/A (optional) option can only be added to a standard question.',
};
/**
 * True when an id is worth looking up. TypeORM 0.3 DROPS an `undefined` condition from a
 * where clause rather than matching nothing, so `findOne({ where: { id: undefined } })`
 * degenerates into "first row in the table" and returns an ARBITRARY question. Callers must
 * use this to decide whether to query at all - never hand an unchecked id to `findOne`.
 */
export const isResolvableParentId = (privacyIssueAnswerInputFieldId) => (typeof privacyIssueAnswerInputFieldId === 'string' && privacyIssueAnswerInputFieldId.trim().length > 0);
/**
 * Decides whether an N/A ("optional") option submission may proceed, given the parent id it
 * carried and the type of the question that id resolved to (`undefined`/`null` when nothing
 * resolved - see `isResolvableParentId` for why the caller may not even have queried).
 *
 * The guard FAILS CLOSED: an id it cannot resolve is rejected rather than waved through. A
 * truthiness check on the loaded question - the shape this replaced - let both a missing id
 * and an unresolvable one pass, which is the bypass this closes; combined with the TypeORM
 * behaviour above, a missing id could also validate the option against an unrelated question
 * and reject a perfectly legal submission.
 */
export const checkNaOptionParent = (privacyIssueAnswerInputFieldId, privacyIssueType) => {
    const reject = (reason) => ({
        allowed: false,
        reason,
        message: NA_OPTION_PARENT_REJECTION_MESSAGES[reason],
    });
    if (!isResolvableParentId(privacyIssueAnswerInputFieldId)) {
        return reject(NaOptionParentRejection.MISSING_PARENT_ID);
    }
    if (!privacyIssueType) {
        return reject(NaOptionParentRejection.UNRESOLVED_PARENT);
    }
    if (privacyIssueType !== STANDARD_PRIVACY_ISSUE_TYPE) {
        return reject(NaOptionParentRejection.NON_STANDARD_PARENT);
    }
    return { allowed: true };
};
//# sourceMappingURL=index.js.map